import {useEffect, useCallback} from 'react';

import {SidePanelId} from '~shared/models';

import {isBoolean} from '~ui/utils';
import {useAppDispatch, useStateSelector} from '~ui/hooks';
import {updateUserSettings} from '~ui/store/slices/user-settings';

import {usePanel} from './use-panel';

type HideByType = Partial<Record<SidePanelId, boolean>>;

export const useSidePanel = (defaultSidePanelId?: SidePanelId, hideByType?: HideByType) => {
    const {sidePanel} = useStateSelector((state) => state.userSettings);

    const {panelVisible, activePanel, switchPanel, hidePanel, showPanel} =
        usePanel<SidePanelId>(defaultSidePanelId);

    const settingsActiveSidePanel = sidePanel?.active;
    const settingsSidePanelVisible = sidePanel?.visible;

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (hideByType && settingsActiveSidePanel && hideByType[settingsActiveSidePanel]) {
            hidePanel();

            return;
        }

        if (!isBoolean(settingsSidePanelVisible)) {
            return;
        }

        if (settingsSidePanelVisible) {
            showPanel(settingsActiveSidePanel);
        } else {
            hidePanel();
        }
    }, [showPanel, hidePanel, settingsActiveSidePanel, settingsSidePanelVisible, hideByType]);

    const showSidePanel = useCallback(
        (id?: SidePanelId) => {
            showPanel(id);
            dispatch(
                updateUserSettings({
                    sidePanel: {
                        visible: true,
                        active: id,
                    },
                }),
            );
        },
        [dispatch, showPanel],
    );

    const hideSidePanel = useCallback(() => {
        hidePanel();
        dispatch(
            updateUserSettings({
                sidePanel: {
                    visible: false,
                },
            }),
        );
    }, [dispatch, hidePanel]);

    const switchSidePanel = useCallback(
        (id: SidePanelId) => {
            switchPanel(id, (visible) => {
                dispatch(
                    updateUserSettings({
                        sidePanel: {
                            visible,
                            active: id,
                        },
                    }),
                );
            });
        },
        [dispatch, switchPanel],
    );

    return {
        sidePanelVisible: panelVisible,
        activeSidePanel: activePanel,
        showSidePanel,
        hideSidePanel,
        switchSidePanel,
    };
};
